import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {SearchResult} from '../_models/SearchResult';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {HubConnection, HubConnectionBuilder} from '@microsoft/signalr';
import {OrganizzazioniService} from './organizzazioni.service';
import {SaveMessage} from '../_helpers/save-message';
import {NcScheda} from '../_models/NcScheda';
import {NcTipiSchede} from '../_models/NcTipiSchede';
import {NcCodici} from '../_models/NcCodici';
import {NcCodice} from '../_models/NcCodice';
import * as signalR from "@microsoft/signalr";
import {OrdineGroup} from "../_models/OrdineGroup";
import {ControlloQualita} from "../_models/ControlloQualita";

@Injectable({ providedIn: 'root' })
export class NonconformeService {
  public statoSchedaNC: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public lastSchedaNCCreated: BehaviorSubject<NcScheda> = new BehaviorSubject<NcScheda>(null);
  public lastSchedaNCUpdated: BehaviorSubject<NcScheda> = new BehaviorSubject<NcScheda>(null);
  public lastSchedaNCDeleted: BehaviorSubject<NcScheda> = new BehaviorSubject<NcScheda>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {}

  public startConnection() {
    const retryTimes = [0, 3000, 10000, 60000];
    return new Promise((resolve, reject) => {
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${environment.localApiUrl}hubs/ncscheda`, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          withCredentials: false
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: context => {
            const index = context.previousRetryCount < retryTimes.length ? context.previousRetryCount : retryTimes.length - 1;
            return retryTimes[index];
          }
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.hubConnection.onclose(() => this.hubState.next(0));
      this.hubConnection.onreconnected(() => this.hubState.next(1));
      this.hubConnection.onreconnecting(() => this.hubState.next(-1));

      this.hubConnection.start()
        .then(() => {
          this.hubState.next(1);
          resolve(true);
        })
        .catch((err: any) => reject(err));

      this.hubConnection.on('SendCreated',(scheda: NcScheda) => {
        if (scheda.organizzazioneId === this.organizzazioniService.organizzazioneId) {
          this.saveMessage.messageInfo('Nuova scheda NC creata.');
          this.lastSchedaNCCreated.next(scheda);
        }
      });

      this.hubConnection.on('SendUpdated',(scheda: NcScheda) => {
        if (scheda.organizzazioneId === this.organizzazioniService.organizzazioneId) {
          this.saveMessage.messageInfo('Scheda NC aggiornata.');
          // Il payload include idSchedaNC e dataRegistrazione aggiornati dal worker dopo il reinvio a ESOLVER
          this.lastSchedaNCUpdated.next(scheda);
        }
      });

      this.hubConnection.on('SendDeleted',(scheda: NcScheda) => {
        if (scheda.organizzazioneId === this.organizzazioniService.organizzazioneId) {
          this.saveMessage.messageInfo('Scheda NC eliminata.');
          this.lastSchedaNCDeleted.next(scheda);
        }
      });
    });
  }

  async getNcTipiSchede(orgId: number) {
    return firstValueFrom(
      this.http.get<SearchResult<NcTipiSchede>>(`${environment.localApiUrl}api/Qualita/NcTipiSchede/${orgId}`)
    );
  }

  async getNcCodici(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<NcCodici>>(`${environment.localApiUrl}api/Qualita/NcCodici/${orgId}`));
  }

  // Anagrafica locale codici NC (feature 027).
  // risorsaId (feature 116, issue #3350) è opzionale e in coda: viene incluso nel body solo quando
  // > 0, così che i chiamanti che non lo passano producano la stessa identica richiesta di prima.
  async getNcCodiciLocali(orgId: number, search = '', page = 0, count = 0,
                          sortField = 'codice', sortAsc = true, excludeInactive = false, onlyEsolver = false,
                          risorsaId = 0) {
    const body = {search, page, count, sortField, sortAsc, excludeInactive, onlyEsolver};
    return firstValueFrom(this.http
      .post<SearchResult<NcCodice>>(`${environment.localApiUrl}api/NcCodici/Filtered/${orgId}`,
        risorsaId > 0 ? {...body, risorsaId} : body));
  }

  async getNcCodiciEsolver(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<NcCodici>>(`${environment.localApiUrl}api/NcCodici/Esolver/${orgId}`));
  }

  async createNcCodice(ncCodice: NcCodice) {
    return firstValueFrom(this.http
      .post<NcCodice>(`${environment.localApiUrl}api/NcCodici/Create`, ncCodice));
  }

  async updateNcCodice(ncCodice: NcCodice) {
    return firstValueFrom(this.http
      .post<NcCodice>(`${environment.localApiUrl}api/NcCodici/Update`, ncCodice));
  }

  async deleteNcCodice(orgId: number, code: string) {
    return firstValueFrom(this.http
      .get<string>(`${environment.localApiUrl}api/NcCodici/Delete/${orgId}/${code}`));
  }

  async createNcSchedaAsync(ncScheda: NcScheda) {
    return firstValueFrom(this.http.post<NcScheda>(`${environment.localApiUrl}api/Qualita/CreateNcScheda`, ncScheda));
  }

  // Accoda un Update su NcSchedaMqWorker (aggiornamento campi e/o reinvio a ESOLVER gestiti dal worker)
  async updateNcSchedaAsync(ncScheda: NcScheda) {
    return firstValueFrom(this.http.post<NcScheda>(`${environment.localApiUrl}api/Qualita/UpdateNcScheda`, ncScheda));
  }

  // Logica condivisa di (re)invio a ESOLVER (FR-006): azzera idSchedaNC come segnale (CL-06)
  // e accoda l'Update; il worker, trovando idSchedaNC == 0, esegue CreateNcSchedaTestata.
  async reinviaNcEsolver(scheda: NcScheda): Promise<void> {
    scheda.idSchedaNC = 0;
    try {
      await this.updateNcSchedaAsync(scheda);
    } catch {
      this.saveMessage.messageError('Errore durante il reinvio della scheda NC a ESOLVER.');
    }
  }

  async sendNcSchedaAsync(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, ncScheda: NcScheda, userName: string) {
    return firstValueFrom(this.http.post<ControlloQualita>(`${environment.localApiUrl}api/Qualita/SendNcScheda`,
      { orgId, dataEvento, ordineGroup, ncScheda, userName }));
  }

  async getSchedeNC(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(this.http
      .post<SearchResult<NcScheda>>(`${environment.localApiUrl}api/Qualita/SearchSchedeNC/${orgId}`,
        {search, page, count, sortField, sortAsc}));
  }

  getIdOrdine(rifOrdine: string) {
    if (rifOrdine.length < 13) {
      return {idDocumento: 0, idRiga: 0};
    }
    return {idDocumento: parseInt(rifOrdine.substring(0,9),10), idRiga: parseInt(rifOrdine.substring(9,13),10)};
  }
}
